import React, { FC, ChangeEvent, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import * as Apis from 'apps/fleet-planner/utils/api'
import { TextInput } from 'components/atoms'
import { Button } from 'transfix-ui'
import { VALIDATOR_PATTERNS } from 'utils/validators'

interface IPasswordForm {
  currentPassword: string
  newPassword: string
}

export interface IOnCloseOptions {
  resetForm: () => void
}

interface IResetPasswordProps {
  onClose: (options: IOnCloseOptions) => void
  onCancel?: () => void
  hideCancelButton?: boolean
}

const NEW_PASSWORD_VALIDATION_MESSAGE =
  'Pick an 8-character length password that has at least one uppercase letter, a lowercase letter, a number and a special character (!?@#$).'
const CURRENT_PASSWORD_VALIDATION_MESSAGE = 'Invalid Password'

const PasswordFormSchema = yup.object<IPasswordForm>({
  currentPassword: yup.string().required('Current Password is required.'),
  newPassword: yup
    .string()
    .required()
    .matches(VALIDATOR_PATTERNS.PASSWORD, NEW_PASSWORD_VALIDATION_MESSAGE),
})

type PasswordFormSchemaType = yup.InferType<typeof PasswordFormSchema>

export const ResetPassword: FC<IResetPasswordProps> = ({
  onClose = () => {},
  onCancel,
  hideCancelButton = false,
}) => {
  const {
    clearErrors,
    control,
    errors: formErrors,
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: yupResolver(PasswordFormSchema),
    defaultValues: PasswordFormSchema.cast({
      currentPassword: '',
      newPassword: '',
    }),
  })

  const submitHandler = async (data: PasswordFormSchemaType) => {
    const { currentPassword, newPassword } = data || {}
    try {
      await Apis.updateUserPassword({
        user: {
          currentPassword,
          password: newPassword,
          passwordConfirmation: newPassword,
        },
      })
      onClose({ resetForm: reset })
    } catch (e) {
      setValue('currentPassword', '')
      setError('currentPassword', {
        type: 'string',
        message: CURRENT_PASSWORD_VALIDATION_MESSAGE,
      })
    }
  }

  // If newPassword value is invalid, we clear the input for the user
  useEffect(() => {
    if (formErrors?.newPassword) {
      reset({ currentPassword: getValues('currentPassword'), newPassword: '' }, { errors: true })
    }
  }, [formErrors?.newPassword, reset, getValues])

  return (
    <>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className='row'>
          <div className='col-md-5 mt4'>
            <Controller
              defaultValue=''
              control={control}
              name='currentPassword'
              render={({ onChange, value }) => {
                const changeHandler = ({
                  target: { value: val },
                }: ChangeEvent<HTMLInputElement>) => {
                  if (formErrors?.currentPassword) clearErrors('currentPassword')
                  onChange(val)
                }
                return (
                  <TextInput
                    large
                    dataTest='current-password'
                    id='currentPassword'
                    onChange={changeHandler}
                    value={value}
                    type='password'
                    label='Current Password'
                    placeholder='Current Password'
                    invalid={!!formErrors?.currentPassword}
                    validationMessage={formErrors?.currentPassword?.message}
                    className='fs-exclude'
                  />
                )
              }}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-md-5 mt4'>
            <Controller
              defaultValue=''
              control={control}
              name='newPassword'
              render={({ onChange, value }) => {
                const changeHandler = ({
                  target: { value: val },
                }: ChangeEvent<HTMLInputElement>) => {
                  if (formErrors?.newPassword) clearErrors('newPassword')
                  onChange(val)
                }
                return (
                  <TextInput
                    large
                    dataTest='new-password'
                    id='newPassword'
                    onChange={changeHandler}
                    value={value}
                    type='password'
                    label='New Password'
                    placeholder='New Password'
                    invalid={!!formErrors?.newPassword}
                    helperMessage={NEW_PASSWORD_VALIDATION_MESSAGE}
                    validationMessage={NEW_PASSWORD_VALIDATION_MESSAGE}
                  />
                )
              }}
            />
          </div>
        </div>
        <div className='row fr'>
          <div className='mt4 pa2'>
            {!hideCancelButton && (
              <Button color='tertiary' size='large' onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              color='primary'
              data-test='submit-password'
              size='large'
              onClick={handleSubmit(submitHandler)}>
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </>
  )
}
